import { useState } from "react";
import { UseFormReset } from "react-hook-form";
import { TypedFilter } from "odata-query";
import { taskService } from "@miplatform/api";
import { ENTITY_NAMES, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IStepTemplateData, IStepTemplateResponse } from "@miplatform/types";

export const useStepTemplates = ({
  reset = () => null,
  assignmentTemplateId = "",
}: IUseStepTemplates = {}) => {
  const [stepTemplates, setStepTemplates] = useState<IStepTemplateResponse[]>(
    []
  );
  const [stepTemplateDetails, setStepTemplateDetails] =
    useState<IStepTemplateResponse | null>(null);
  const [refresh, setRefresh] = useState(false);
  const [filter, setFilter] = useState<TypedFilter<IStepTemplateResponse>>({});

  const stateManager = useComponentState();

  const getStepTemplates = () => {
    if (!assignmentTemplateId) return Promise.resolve(null);

    return stateManager.run(async () => {
      const response = await taskService.assignmentTemplates.getStepTemplates({
        assignmentTemplateId,
        filters: filter,
      });
      if (response?.status === HTTP_STATUSES.OK) {
        setStepTemplates(response.data);
      }
      return response;
    });
  };

  const getStepTemplateDetails = (stepTemplateId: string) => {
    if (!assignmentTemplateId) return;

    stateManager.run(async () => {
      const response =
        await taskService.assignmentTemplates.getStepTemplateById({
          assignmentTemplateId,
          stepTemplateId,
        });
      if (response?.status === HTTP_STATUSES.OK) {
        setStepTemplateDetails(response.data);
        reset(response.data);
      }
    });
  };

  const createStepTemplate = (data: IStepTemplateData) => {
    if (!assignmentTemplateId) return Promise.resolve(null);

    return stateManager.run(async () => {
      const response =
        await taskService.assignmentTemplates.createStepTemplateItem({
          templateId: assignmentTemplateId,
          data,
          toastMessageConfig: {
            success: {
              text: `${data.title} was created successfully.`,
            },
          },
        });

      if (response.status === HTTP_STATUSES.CREATED) {
        setRefresh((prev) => !prev);
      }

      return response;
    });
  };

  const updateStepTemplate = (data: Partial<IStepTemplateData>) => {
    if (!assignmentTemplateId) return Promise.resolve(null);

    return stateManager.run(async () => {
      const response =
        await taskService.assignmentTemplates.updateStepTemplateItem({
          assignmentTemplateId,
          stepTemplateId: data.id,
          data,
          toastMessageConfig: {
            success: {
              text: `${data.title} was updated successfully.`,
            },
          },
        });

      if (response.status === HTTP_STATUSES.OK) {
        setStepTemplateDetails(response.data);
        reset(response.data);
        setRefresh((prev) => !prev);
      }

      return response;
    });
  };

  const deleteStepTemplate = (
    stepTemplateId: string,
    data: { modifiedOn: string }
  ) => {
    if (!assignmentTemplateId) return Promise.resolve(null);

    return stateManager.run(async () => {
      const response =
        await taskService.assignmentTemplates.deleteStepTemplateItem({
          assignmentTemplateId,
          stepTemplateId,
          data,
          toastMessageConfig: {
            success: {
              text: `${ENTITY_NAMES.STEP_TEMPLATE} was deleted successfully.`,
            },
            failure: {
              text: `Failed to delete ${ENTITY_NAMES.STEP_TEMPLATE.toLowerCase()}.`,
            },
          },
        });

      if (response.status === HTTP_STATUSES.DELETED) {
        setRefresh((prev) => !prev);
      }

      return response;
    });
  };

  const reorderStepTemplates = (
    data: Partial<IStepTemplateData>[],
    modifiedOn: string
  ) => {
    if (!assignmentTemplateId) return Promise.resolve(null);

    return stateManager.run(async () => {
      const response =
        await taskService.assignmentTemplates.reorderStepTemplates({
          assignmentTemplateId,
          data,
          modifiedOn,
          toastMessageConfig: {
            failure: {
              text: `Failed to reorder ${ENTITY_NAMES.STEP_TEMPLATES.toLowerCase()}.`,
            },
          },
        });

      if (response.status === HTTP_STATUSES.NO_CONTENT) {
        setRefresh((prev) => !prev);
      }

      return response;
    });
  };

  const getStepTemplatesByModule = (
    module: string,
    assignTemplateId?: string
  ) => {
    const filters = assignTemplateId
      ? {
          assignmentTemplateId: {
            type: "guid",
            value: assignTemplateId,
          },
        }
      : undefined;

    return stateManager.run(async () => {
      return taskService.stepTemplates.getByModule({
        module,
        sort: "priorityOrder",
        filters,
      });
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    stepTemplates,
    setStepTemplates,
    stepTemplateDetails,
    setStepTemplateDetails,
    getStepTemplates,
    getStepTemplateDetails,
    createStepTemplate,
    updateStepTemplate,
    deleteStepTemplate,
    reorderStepTemplates,
    refresh,
    setRefresh,
    filter,
    setFilter,
    getStepTemplatesByModule,
  };
};

interface IUseStepTemplates {
  reset?: UseFormReset<IStepTemplateData>;
  assignmentTemplateId?: string;
}
